import { Button, Tooltip } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import ResponsiveDrawer from "../../components/ClippedResponsiveDrawer";
import { AxiosRequestConfig } from "axios";
import { useEffect, useState } from "react";
import { AXIOS_INSTANCE, BASE_URL } from "../../App";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import TableAbstraction from "../../componentUtils/TableAbstraction";
import Header, { tableHeightReducePx } from "./Header";
import TableViewIcon from "@mui/icons-material/TableView";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

interface ReportingState {
  isLoaded: boolean;
  data: ReportingRow[];
  matDesc: MatDesc[];
}

interface MatDesc {
  materialNum: string;
  materialNumShort: string;
  description: string;
}

interface ReportingRow {
  salesOffice: string;
  masterRoute: string;
  customerNum: string;
  name: string;
  address: string;
  city: string;
  zip: string;
  location: string;
  subtradeChannel: string;
  customerErrors: number;
  materialErrors: number;
  equipmentNum: string;
  equipmentStatus: string;
  equipmentActive: number;
  assetNum: string;
  manufacturer: string;
  model: string;
  telemeterModel: string;
  telemeterSerial: string;
  dexType: string;
  dexDeviceId: string;
  consumption: number;
  changerFund: number;
  settlementMethod: number;
  materialNum: string;
  materialNumShort: string;
  materialDescription: string;
  allocationRatio: number;
  salesRatio: number;
  vendPrice: number;
  columnNum: string;
  consignment: number;
  lastClosingInventory: number;
  inventory: number;
  lastVisitDate: Date;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    tokenBlock: {
      width: "500px",
      overflowWrap: "break-word",
    },
    typography: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
  })
);

async function getReportingData(salesOrg: string, salesOffices: string[], routes: string[], stcs: string[]) {
  const configReporting: AxiosRequestConfig = {
    method: "GET",
    params: {
      clientId: "100",
      salesOrg: salesOrg,
      salesOffices: JSON.stringify(salesOffices),
      routes: JSON.stringify(routes),
      stcs: JSON.stringify(stcs),
    },
    url: `${BASE_URL}/api/reporting/ut`,
  };

  return await AXIOS_INSTANCE(configReporting).then((res: any) => res.data);
}

export default function UtilizationReporting() {
  const classes = useStyles();
  const [state, setState] = useState<ReportingState>({
    isLoaded: false,
    data: [],
    matDesc: [],
  });

  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");

  const onSelectionChange = (salesOffices: string[], routes: string[], subtradeChannels: string[]) => {
    const sOrg = salesOrg.toString();
    if (salesOffices.length > 0) {
      // console.log(salesOffices, routes, subtradeChannels);
      getReportingData(sOrg, salesOffices, routes, subtradeChannels).then((v) => {
        setState({ isLoaded: true, data: v.data, matDesc: v.matDesc });
      });
    }
  };

  const DownloadButton = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Machine Configuration Report");

    worksheet.columns = [
      { header: "Customer #", key: "customerNum", width: 20 },
      { header: "Equipment #", key: "equipmentNum", width: 20 },
      { header: "Equipment Active", key: "equipmentActive", width: 10 },
      { header: "Equipment Status", key: "equipmentStatus", width: 10 },
      { header: "Asset #", key: "assetNum" },
      { header: "Manufacturer", key: "manufacturer", width: 20 },
      { header: "Model", key: "model", width: 10 },
      { header: "Sales Office", key: "salesOffice", width: 10 },
      { header: "Master Route", key: "masterRoute", width: 10 },
      { header: "Name", key: "name", width: 10 },
      { header: "Address", key: "address", width: 10 },
      { header: "City", key: "city", width: 10 },
      { header: "State/Zip", key: "zip", width: 10 },
      { header: "Subtrade Channel", key: "subtradeChannel", width: 10 },
      { header: "Customer Errors", key: "customerErrors", width: 10 },
      { header: "Material Errors", key: "materialErrors", width: 10 },
      { header: "Machine Location", key: "location", width: 10 },
      { header: "Consumption", key: "consumption", width: 10 },
      { header: "Telemeter Model", key: "telemeterModel", width: 10 },
      { header: "Telemeter Serial #", key: "telemeterSerial", width: 10 },
      { header: "Dex Type", key: "dexType", width: 10 },
      { header: "Dex Device Id", key: "dexDeviceId", width: 10 },
      { header: "Changer Fund", key: "changerFund", width: 10 },
      { header: "Settlement Method", key: "settlementMethod", width: 10 },
      { header: "Material #", key: "materialNum", width: 10 },
      { header: "Material Description", key: "materialDescription", width: 10 },
      { header: "Column #", key: "columnNum", width: 10 },
      { header: "Consignment", key: "consignment", width: 10 },
      { header: "Vend Price", key: "vendPrice", width: 10 },
      { header: "Last Visit Date", key: "lastVisitDate", width: 10 },
    ];

    const data = state.data;
    const filterdData: any = [];
    data.forEach((ele: any) => {
      let dataObject: any = {
        customerNum: ele.customerNum,
        equipmentNum: ele.equipmentNum,
        equipmentActive: ele.equipmentActive,
        equipmentStatus: ele.equipmentStatus,
        assetNum: ele.assetNum,
        manufacturer: ele.manufacturer,
        model: ele.model,
        salesOffice: ele.salesOffice,
        masterRoute: ele.masterRoute,
        name: ele.name,
        address: ele.address,
        city: ele.city,
        zip: ele.zip,
        subtradeChannel: ele.subtradeChannel,
        customerErrors: ele.customerErrors,
        materialErrors: ele.materialErrors,
        location: ele.location,
        consumption: ele.consumption,
        telemeterModel: ele.telemeterModel,
        telemeterSerial: ele.telemeterSerial,
        dexType: ele.dexType,
        dexDeviceId: ele.dexDeviceId,
        changerFund: ele.changerFund,
        settlementMethod: ele.settlementMethod,
        materialNum: ele.materialNum,
        materialDescription: ele.materialDescription,
        columnNum: ele.columnNum,
        consignment: ele.consignment,
        vendPrice: ele.vendPrice,
        lastVisitDate: ele.lastVisitDate,
      };

      filterdData.push(dataObject);
    });

    //inserting the rows starting from porition 2 so that headers would not be override.
    worksheet.insertRows(2, filterdData);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "UtilizationReport.xlsx");
  };

  return (
    <ResponsiveDrawer title="Reporting">
      <div className={classes.rootContainer}>
        <Header onSelectionChange={onSelectionChange} />

        <TableAbstraction
          settingsKey="RPT_UTIL"
          options={{
            serverSide: false,
            responsive: "vertical",
            selectableRows: "none",
            rowsPerPage: 100,
            sortOrder: [{ name: "customerNum", direction: "asc" }],
            tableHeightReducePx,
            customToolbar: () => {
              return (
                <Tooltip title={`Import Seasonality`}>
                  <Button component="label" style={{ marginRight: 10 }} onClick={DownloadButton}>
                    <TableViewIcon /> Download Data
                  </Button>
                </Tooltip>
              );
            },
          }}
          title="Vending Machine Utilization (BETA)"
          columns={[
            {
              label: "Customer #",
              name: "customerNum",
            },
            {
              label: "Equipment #",
              name: "equipmentNum",
            },
            {
              label: "Equipment Active",
              name: "equipmentActive",
            },
            {
              label: "Equipment Status",
              name: "equipmentStatus",
            },
            {
              label: "Asset #",
              name: "assetNum",
            },
            {
              label: "Manufacturer",
              name: "manufacturer",
            },
            {
              label: "Model",
              name: "model",
            },
            {
              label: "Sales Office",
              name: "salesOffice",
            },
            {
              label: "Master Route",
              name: "masterRoute",
            },
            {
              label: "Name",
              name: "name",
            },
            {
              label: "Address",
              name: "address",
            },
            {
              label: "City",
              name: "city",
            },
            {
              label: "State/Zip",
              name: "zip",
            },
            {
              label: "Subtrade Channel",
              name: "subtradeChannel",
            },
            {
              label: "Customer Errors",
              name: "customerErrors",
            },
            {
              label: "Material Errors",
              name: "materialErrors",
            },
            {
              label: "Machine Location",
              name: "location",
            },
            {
              label: "Consumption",
              name: "consumption",
            },
            {
              label: "Telemeter Model",
              name: "telemeterModel",
            },
            {
              label: "Telemeter Serial #",
              name: "telemeterSerial",
            },
            {
              label: "DEX Type",
              name: "dexType",
            },
            {
              label: "DEX Device Id",
              name: "dexDeviceId",
            },
            {
              label: "Changer Fund",
              name: "changerFund",
            },
            {
              label: "Settlement Method",
              name: "settlementMethod",
            },
            {
              label: "Material #",
              name: "materialNumShort",
            },
            {
              label: "Material Description",
              name: "materialDescription",
            },
            {
              label: "Column #",
              name: "columnNum",
            },
            {
              label: "Consignment",
              name: "consignment",
            },
            {
              label: "Vend Price",
              name: "vendPrice",
            },
            {
              label: "Last Visit Date",
              name: "lastVisitDate",
            },
          ]}
          data={state.data}
        />
      </div>
    </ResponsiveDrawer>
  );
}
